/**
 * Novo ativo (engenharia; campo cadastra como pendente) — Nexora Grid (frontend, US-406, US-4603).
 *
 * Criado em: 16/10/2026 · Última modificação: 02/11/2026 · Versão: 2.0.0
 *
 * v2.0 (Sprint 46): campos em grupos com validação na hora, prévia 3D do tipo escolhido, mapa da posição e rodapé
 * fixo. Funciona no celular (cadastro de campo). Mesma chamada de antes: `POST /v1/ativos` (entra como pendente).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ErroApi } from "../api";
import { avisar, CabecalhoTela, Campo, GrupoCampos, RodapeFormulario, useAvisoAoSair } from "../componentes/Tela";
import { PreviaAtivo3D } from "../componentes/PreviaAtivo3D";
import { useSessao } from "../sessao";
import { CampoSetor } from "./CampoSetor";
import { MapaPosicao } from "./MapaPosicao";
import { TIPOS, type Ativo } from "./tipos";

// centro de Costa Rica/MS (única operadora da PoC, `empresa01`): o ponto começa ali e a pessoa arrasta
const POSICAO_INICIAL = { latitude: -18.5446, longitude: -53.1293 };

export function Novo() {
  const { empresa } = useSessao();
  const navegar = useNavigate();
  const [identificacao, setIdentificacao] = useState("");
  const [tipo, setTipo] = useState("bomba");
  const [nome, setNome] = useState("");
  const [setor, setSetor] = useState("");
  const [posicao, setPosicao] = useState(POSICAO_INICIAL);
  const [tocado, setTocado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const sujo = !!(identificacao || nome || setor) || posicao !== POSICAO_INICIAL;
  useAvisoAoSair(sujo && !salvando);
  const erroId = useMemo(() => (!identificacao.trim() ? "Informe a identificação (ex.: PCR-04)."
    : !/^[A-Za-z0-9][A-Za-z0-9 ._/-]{0,59}$/.test(identificacao.trim()) ? "Use letras, números, espaço, ponto, barra ou hífen." : null), [identificacao]);

  const criar = async () => {
    setErro(null);
    setSalvando(true);
    try {
      const a = await api<Ativo>("/v1/ativos", { corpo: { empresa, dados: { identificacao: identificacao.trim(), tipo, nome, setor, ...posicao } } });
      avisar(`${a.dados.identificacao} cadastrado como pendente`);
      navegar(`/cadastro/${a.id}?aba=ficha`);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : String(e));
      setSalvando(false);
    }
  };
  return (
    <section className="tl-pagina">
      <CabecalhoTela voltar="/cadastro" migalhas={[{ texto: "Cadastro", para: "/cadastro" }, { texto: "Ativos", para: "/cadastro" }, { texto: "Novo ativo" }]}
        titulo="Novo ativo" subtitulo="Entra como pendente; a ficha técnica vem em seguida" />
      <div className="ficha-grade">
        <div className="ficha-lado">
          <div className="tl-bloco"><PreviaAtivo3D tipo={tipo} identificacao={identificacao} estado="semtel" rotulo={TIPOS[tipo] ?? tipo} altura={260} arrastar /></div>
        </div>
        <section className="tl-bloco" aria-label="Dados do novo ativo">
          <div className="tl-painel-aba">
            <GrupoCampos titulo="Identificação">
              <Campo id="n-id" rotulo="Identificação" erro={tocado ? erroId : null} ajuda="Como está no desenho ou na placa do equipamento">
                <input id="n-id" value={identificacao} onChange={(e) => setIdentificacao(e.target.value)} onBlur={() => setTocado(true)}
                  placeholder="ex.: PCR-04" aria-invalid={tocado && !!erroId} autoFocus /></Campo>
              <Campo id="n-tipo" rotulo="Tipo">
                <select id="n-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  {Object.entries(TIPOS).map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></Campo>
              <Campo id="n-nome" rotulo="Nome"><input id="n-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="ex.: Poço Jardim das Flores" /></Campo>
              <Campo id="n-setor" rotulo="Setor"><CampoSetor empresa={empresa} id="n-setor" valor={setor} aoMudar={setSetor} /></Campo>
            </GrupoCampos>
            <GrupoCampos titulo="Localização">
              <div className="tl-campo largo">
                <span className="nx-suave" style={{ fontSize: 12 }}>Posição: <span className="tl-mono">{posicao.latitude.toFixed(6)}, {posicao.longitude.toFixed(6)}</span>
                  {" "}· clique ou arraste o ponto · a cota vem do relevo</span>
                <MapaPosicao {...posicao} editavel aoMudar={(latitude, longitude) => setPosicao({ latitude, longitude })} />
              </div>
            </GrupoCampos>
            {erro && <p className="erro" role="alert" style={{ margin: 0 }}>{erro}</p>}
          </div>
          <RodapeFormulario sujo={sujo} valido={!erroId && !!empresa} salvando={salvando} rotuloSalvar="Cadastrar"
            aoCancelar={() => navegar("/cadastro")} aoSalvar={() => { setTocado(true); if (!erroId) void criar(); }} />
        </section>
      </div>
    </section>
  );
}
